/**
 * Link preview metadata fetcher.
 *
 * We use Microlink's free API to grab og:title / og:image / og:description
 * for any URL the user pastes into a note. Microlink runs server-side so
 * it bypasses CORS and works in the browser without our own proxy.
 *
 * Free tier: 50 requests/day per IP. Plenty for a personal note pad —
 * each URL is cached locally (web: localStorage; native: AsyncStorage
 * fallback elided for now) so we only fetch each URL once.
 */

import { Platform } from 'react-native';

export interface LinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  publisher: string | null;
  /** Domain hostname (e.g. "todoist.com") — handy for the card footer. */
  host: string | null;
  fetched_at: number;
}

const CACHE_KEY_PREFIX = 'homestead.link-preview.';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // a week

/**
 * Treat the body as "just a link" when it contains exactly one URL and
 * nothing else (after stripping the HTML chrome). This is the case
 * where we want to render a beefy preview card instead of inline text.
 */
export function extractSingleUrl(body: string): string | null {
  if (!body) return null;
  const plain = body
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  // Match a single URL token at the start; allow trailing whitespace.
  const m = /^(https?:\/\/\S+)$/i.exec(plain);
  if (!m) return null;
  return m[1];
}

/**
 * Read a cached preview from localStorage. Returns null if not cached,
 * cache expired, or storage unavailable.
 */
function readCache(url: string): LinkPreview | null {
  if (Platform.OS !== 'web') return null;
  try {
    // eslint-disable-next-line no-undef
    const raw = window.localStorage.getItem(CACHE_KEY_PREFIX + url);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LinkPreview;
    if (Date.now() - parsed.fetched_at > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(url: string, preview: LinkPreview): void {
  if (Platform.OS !== 'web') return;
  try {
    // eslint-disable-next-line no-undef
    window.localStorage.setItem(CACHE_KEY_PREFIX + url, JSON.stringify(preview));
  } catch {
    // Storage full / disabled / private mode — best effort, ignore.
  }
}

/**
 * Fetch + cache a single URL's OG metadata. Returns null on failure
 * (including network errors, blocked CORS, quota exhausted). The
 * caller renders a fallback link card when the result is null.
 */
export async function fetchLinkPreview(url: string): Promise<LinkPreview | null> {
  const cached = readCache(url);
  if (cached) return cached;

  if (Platform.OS !== 'web') return null;

  try {
    const api = `https://api.microlink.io/?url=${encodeURIComponent(url)}`;
    const res = await fetch(api);
    if (!res.ok) return null;
    const json = await res.json();
    if (json?.status !== 'success' || !json.data) return null;
    const d = json.data;
    let host: string | null = null;
    try {
      host = new URL(url).hostname.replace(/^www\./, '');
    } catch {
      host = null;
    }
    const preview: LinkPreview = {
      url,
      title: typeof d.title === 'string' ? d.title : null,
      description: typeof d.description === 'string' ? d.description : null,
      image: typeof d.image?.url === 'string' ? d.image.url : null,
      publisher: typeof d.publisher === 'string' ? d.publisher : null,
      host,
      fetched_at: Date.now(),
    };
    writeCache(url, preview);
    return preview;
  } catch {
    return null;
  }
}
